import { Plus, Trash2 } from "lucide-react"

import { Boton } from "@/componentes/ui/Boton"

import { type CampoPlantilla, TIPOS_CAMPO, type TipoCampo } from "./tipos"

/** De «Fecha de inicio» a `fecha_de_inicio`: la clave que se escribe en el texto. */
export function claveDe(etiqueta: string): string {
  return etiqueta
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .replace(/^(\d)/, "c_$1")
}

/**
 * Los datos que una plantilla pide al generarse, además de la ficha.
 *
 * ⚠ **La clave sale de la etiqueta mientras nadie la toque**, y se escribe en el
 * texto como `{clave}`. Una fecha o un monto dan además `{clave_letras}`, que lo
 * compone el servidor.
 */
export function EditorCampos({
  campos,
  onCambiar,
}: {
  campos: CampoPlantilla[]
  onCambiar: (campos: CampoPlantilla[]) => void
}) {
  const poner = (i: number, cambio: Partial<CampoPlantilla>) =>
    onCambiar(campos.map((c, j) => (j === i ? { ...c, ...cambio } : c)))

  return (
    <div className="grid gap-2">
      {campos.length === 0 && (
        <p className="text-sm" style={{ color: "var(--fg-muted)" }}>
          No pide nada: se llena entero con la ficha.
        </p>
      )}
      {campos.map((c, i) => (
        <div
          // La posición es la identidad mientras se edita: la clave cambia al teclear.
          // biome-ignore lint/suspicious/noArrayIndexKey: lista editable sin id
          key={i}
          className="tarjeta grid gap-2 p-3 sm:grid-cols-[2fr_1.4fr_1fr_auto]"
        >
          <div>
            <label htmlFor={`campo-etq-${i}`} className="mb-1 block text-xs uppercase">
              Etiqueta
            </label>
            <input
              id={`campo-etq-${i}`}
              className="campo"
              value={c.etiqueta}
              onChange={(e) => {
                const automatica = c.clave === claveDe(c.etiqueta)
                poner(i, {
                  etiqueta: e.target.value,
                  ...(automatica ? { clave: claveDe(e.target.value) } : {}),
                })
              }}
            />
          </div>
          <div>
            <label htmlFor={`campo-clave-${i}`} className="mb-1 block text-xs uppercase">
              En el texto
            </label>
            <input
              id={`campo-clave-${i}`}
              className="campo font-mono"
              value={c.clave}
              onChange={(e) => poner(i, { clave: claveDe(e.target.value) })}
            />
          </div>
          <div>
            <label htmlFor={`campo-tipo-${i}`} className="mb-1 block text-xs uppercase">
              Tipo
            </label>
            <select
              id={`campo-tipo-${i}`}
              className="campo"
              value={c.tipo}
              onChange={(e) => poner(i, { tipo: e.target.value as TipoCampo })}
            >
              {TIPOS_CAMPO.map((t) => (
                <option key={t.valor} value={t.valor}>
                  {t.etiqueta}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-end gap-2">
            <label className="flex h-9 items-center gap-1.5 text-sm">
              <input
                type="checkbox"
                checked={c.obligatorio}
                onChange={(e) => poner(i, { obligatorio: e.target.checked })}
              />
              Obligatorio
            </label>
            <Boton
              variante="fantasma"
              aria-label={`Quitar ${c.etiqueta || "el campo"}`}
              onClick={() => onCambiar(campos.filter((_, j) => j !== i))}
            >
              <Trash2 size={15} aria-hidden="true" />
            </Boton>
          </div>
          {c.tipo === "lista" && (
            <div className="sm:col-span-4">
              <label htmlFor={`campo-op-${i}`} className="mb-1 block text-xs uppercase">
                Opciones, separadas por coma
              </label>
              <input
                id={`campo-op-${i}`}
                className="campo"
                value={(c.opciones ?? []).join(", ")}
                onChange={(e) =>
                  poner(i, {
                    opciones: e.target.value
                      .split(",")
                      .map((o) => o.trim())
                      .filter(Boolean),
                  })
                }
              />
            </div>
          )}
          <p className="text-xs sm:col-span-4" style={{ color: "var(--fg-muted)" }}>
            Se escribe <code>{`{${c.clave || "clave"}}`}</code>
            {(c.tipo === "fecha" || c.tipo === "monto") && (
              <>
                {" "}
                o <code>{`{${c.clave || "clave"}_letras}`}</code> en letras
              </>
            )}
            .
          </p>
        </div>
      ))}
      <div>
        <Boton
          variante="fantasma"
          onClick={() =>
            onCambiar([...campos, { clave: "", etiqueta: "", tipo: "texto", obligatorio: false }])
          }
        >
          <Plus size={15} aria-hidden="true" />
          Añadir un dato
        </Boton>
      </div>
    </div>
  )
}
